import { useEffect, useRef, useState } from 'react';

import {
  bgmEnabled,
  bgmLoop,
  bgmTitle,
  bgmUrl,
  bgmVolume,
} from 'mastodon/initial_state';

const ENABLED_KEY = 'whippy_bgm_enabled';
const VOLUME_KEY = 'whippy_bgm_volume';
const TIME_KEY = 'whippy_bgm_time';
const URL_KEY = 'whippy_bgm_url';

const clampVolume = value => Math.max(0, Math.min(1, value));

const BackgroundMusic = () => {
  const audioRef = useRef(null);
  const defaultVolume = clampVolume(Number(bgmVolume ?? 35) / 100);
  const savedVolume = Number(localStorage.getItem(VOLUME_KEY));

  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(
    Number.isFinite(savedVolume) ? clampVolume(savedVolume) : defaultVolume
  );

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio || !bgmUrl) {
      return undefined;
    }

    audio.volume = volume;

    const previousUrl = localStorage.getItem(URL_KEY);
    if (previousUrl === bgmUrl) {
      const savedTime = Number(localStorage.getItem(TIME_KEY));
      if (Number.isFinite(savedTime) && savedTime >= 0) {
        audio.currentTime = savedTime;
      }
    } else {
      localStorage.setItem(URL_KEY, bgmUrl);
      localStorage.removeItem(TIME_KEY);
    }

    const shouldPlay = localStorage.getItem(ENABLED_KEY) === 'true';
    if (shouldPlay) {
      audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }

    const savePosition = () => {
      if (Number.isFinite(audio.currentTime)) {
        localStorage.setItem(TIME_KEY, String(audio.currentTime));
      }
    };

    const timer = window.setInterval(savePosition, 3000);
    window.addEventListener('beforeunload', savePosition);

    return () => {
      savePosition();
      window.clearInterval(timer);
      window.removeEventListener('beforeunload', savePosition);
    };
  }, []); // BGM server settings are fixed for the lifetime of this page load.

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }

    localStorage.setItem(VOLUME_KEY, String(volume));
  }, [volume]);

  if (!bgmEnabled || !bgmUrl) {
    return null;
  }

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      audio.play().then(() => {
        setPlaying(true);
        localStorage.setItem(ENABLED_KEY, 'true');
      }).catch(() => setPlaying(false));
    } else {
      audio.pause();
      setPlaying(false);
      localStorage.setItem(ENABLED_KEY, 'false');
    }
  };

  return (
    <div className='background-music-player'>
      <audio
        ref={audioRef}
        src={bgmUrl}
        preload='metadata'
        loop={Boolean(bgmLoop)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          localStorage.setItem(ENABLED_KEY, 'false');
        }}
      />

      <button
        type='button'
        className='background-music-player__button'
        onClick={togglePlayback}
        aria-label={playing ? '배경음악 일시정지' : '배경음악 재생'}
        title={playing ? 'BGM 일시정지' : 'BGM 재생'}
      >
        {playing ? '❚❚' : '▶'}
      </button>

      <div className='background-music-player__title' title={bgmTitle || 'BGM'}>
        ♫ {bgmTitle || 'BGM'}
      </div>

      <input
        className='background-music-player__volume'
        type='range'
        min='0'
        max='1'
        step='0.05'
        value={volume}
        onChange={event => setVolume(clampVolume(Number(event.target.value)))}
        aria-label='BGM 음량'
      />
    </div>
  );
};

export default BackgroundMusic;
